/* global SWDLocalise */
import { gsap } from 'gsap';
import DrawSVGPlugin from 'gsap/DrawSVGPlugin';
import ScrollTrigger from 'gsap/ScrollTrigger';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import Draggable from 'gsap/Draggable';
import lottie from 'lottie-web';
import { preloadImages, debounce, getBBox } from '../../utils'; // use lodash.debounce
import { openModal } from '../modal';

gsap.registerPlugin(DrawSVGPlugin);
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(MotionPathPlugin);
gsap.registerPlugin(Draggable);

const pathContainer = document.querySelector('.js-about-svg-container');

let pathTimeline = null;

const scrollAnims = () => {
    // animate gallery
    gsap.fromTo('.js-about-gallery', {
        x: 0,
    }, {
        x: -400,
        ease: 'none',
        scrollTrigger: {
            trigger: '.js-about-gallery-wrapper',
            scrub: 0.5,
        },
    });

    // animate images
    const images = document.querySelectorAll('.js-about-image');
    images.forEach((image) => {
        gsap.to(image, {
            y: image.dataset.y !== undefined ? Number(image.dataset.y) : 30,
            rotate: image.dataset.rotate !== undefined ? Number(image.dataset.rotate) : 6,
            ease: 'none',
            scrollTrigger: {
                trigger: image,
                start: 'top center',
                end: () => '+=800',
                scrub: true,
            },
        });
    });

    // modal
    const videoModalLink = document.querySelector('.js-about-modal');
    if (videoModalLink) {
        videoModalLink.addEventListener('click', () => {
            const content = `
                <iframe width="640"
                    height="360"
                    src="https://www.youtube.com/embed/5Bq-SacHpeA"
                    frameborder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen></iframe>
            `;

            openModal(
                content,
                false,
                false,
                'c-video-modal',
            );
        });
    }
};

const animatePath = () => {
    const animations = [];
    const pointer = document.querySelector('.js-about-pointer');
    const marks = document.querySelectorAll('.js-about-mark');
    const path = document.querySelector('.js-about-path-mask .js-about-path');

    // setup marks
    marks.forEach((mark) => {
        const text = mark.parentNode.querySelector('p');

        gsap.killTweensOf(mark);
        gsap.killTweensOf(text);

        gsap.set(mark, {
            scale: 0,
            opacity: 0,
        });

        gsap.set(text, {
            y: 30,
            opacity: 0,
        });

        const timeline = gsap.timeline({ paused: 1 });
        timeline.to(mark, {
            duration: 0.2,
            scale: 1,
            opacity: 1,
        }).to(text, {
            duration: 0.4,
            y: 0,
            opacity: 1,
        });

        animations.push(timeline);
    });

    // used to determine if first load
    let loaded = false;

    // setup path
    pathTimeline = gsap.timeline({
        scrollTrigger: {
            trigger: pathContainer,
            start: 'top 200px',
            scrub: 0.5,
            onUpdate: (self) => {
                // run timeline if pointer scrolled past mark
                if (!loaded) {
                    const pointerPos = getBBox(pointer);
                    marks.forEach((mark, x) => {
                        if (pointerPos.y > mark.offsetTop) {
                            animations[x].play();
                        }
                    });
                    loaded = true;
                }

                // run/reverse timeline when pointer collides with mark
                marks.forEach((mark, x) => {
                    if (Draggable.hitTest(pointer, mark)) {
                        if (self.direction === 1) {
                            animations[x].play();
                        } else {
                            animations[x].reverse();
                        }
                    }
                });
            },
        },
    });

    pathTimeline.from(path, {
        drawSVG: 0,
    }, 0).to(pointer, {
        motionPath: {
            path,
            align: path,
            alignOrigin: [0.5, 0.5],
            autoRotate: false,
        },
        transformOrigin: '50% 50%',
    }, 0);
};

const reset = () => {
    // remove SVG if in place
    const originalSVG = document.querySelector('.js-about-svg');
    if (originalSVG) {
        originalSVG.parentNode.removeChild(originalSVG);
    }

    // kill the timeline
    if (pathTimeline !== null) {
        pathTimeline.kill();

        // set to null so the reference is removed
        pathTimeline = null;
    }
};

const makePath = () => {
    // reset
    reset();

    // create SVG
    const aspect = pathContainer.offsetWidth / pathContainer.offsetHeight;
    const adjustedHeight = Math.ceil(pathContainer.offsetWidth / aspect);
    const svg = `
        <svg viewBox="0 0 ${pathContainer.offsetWidth} ${adjustedHeight}"
            class="c-about_path js-about-svg">
            <defs>
                <mask id="theMask" maskUnits="userSpaceOnUse" class="js-about-path-mask"></mask>
            </defs>
            <g id="maskReveal" mask="url(#theMask)" class="js-about-path"></g>
            <circle class="js-about-pointer" r="75" fill="transparent" />
        </svg>
    `;
    pathContainer.innerHTML += svg;

    // get points
    const points = [];
    let coords = document.querySelectorAll('.js-coord');

    if (window.matchMedia('(min-width: 768px)').matches) {
        coords = [...coords].sort((a, b) => Number(a.dataset.order) - Number(b.dataset.order));
    }

    coords.forEach((coord) => {
        const centerX = coord.offsetLeft + coord.offsetWidth / 2;
        const centerY = coord.offsetTop + coord.offsetHeight / 2;
        points.push([centerX, centerY]);
    });

    // the smoothing factor
    const smoothing = 0.2;

    // Properties of a line
    // I:  - pointA (array) [x,y]: coordinates
    //     - pointB (array) [x,y]: coordinates
    // O:  - (object) { length: (integer), angle: (integer) }
    const line = (pointA, pointB) => {
        const lengthX = pointB[0] - pointA[0];
        const lengthY = pointB[1] - pointA[1];
        return {
            length: Math.sqrt((lengthX ** 2) + (lengthY ** 2)),
            angle: Math.atan2(lengthY, lengthX),
        };
    };

    // Create a function to calculate the position of the control point
    // I:  - lineCalc (function)
    //       I:  - pointA (array) [x, y]: coordinates
    //           - pointB (array) [x, y]: coordinates
    //       O:  - (object) { length: (integer), angle: (integer) }
    //     - smooth (float)
    // O:  - (function) closure
    //       I:  - current (array) [x, y]: coordinates
    //           - previous (array) [x, y]: coordinates
    //           - next (array) [x, y]: coordinates
    //           - reverse (boolean, optional): sets the direction
    //       O:  - (array) [x,y]: coordinates
    const controlPoint = (lineCalc, smooth) => (current, previous, next, reverse) => {
        // Replace 'previous' and 'next' with 'current'
        // if they don't exist
        // (when 'current' is the first or last point of the array)
        const p = previous || current;
        const n = next || current;

        // properties of the line between previous and next
        const l = lineCalc(p, n);

        // If is end-control-point, add PI to the angle to go backward
        const angle = l.angle + (reverse ? Math.PI : 0);
        const length = l.length * smooth;

        // The control point position is relative to the current point
        const x = current[0] + Math.cos(angle) * length;
        const y = current[1] + Math.sin(angle) * length;

        return [x, y];
    };

    // Create a function to calculate a bezier curve command
    // I:  - controlPoint (function)
    //       I:  - current (array) [x, y]: current point coordinates
    //           - previous (array) [x, y]: previous point coordinates
    //           - next (array) [x, y]: next point coordinates
    //           - reverse (boolean) to set the direction
    // O:  - (function) closure
    //       I:  - point (array) [x,y]: current point coordinates
    //           - i (integer): index of 'point' in the array 'a'
    //           - a (array): complete array of points coordinates
    //       O:  - (string) 'C x2,y2 x1,y1 x,y': cubic bezier command
    const bezierCommand = (controlPointCalc) => (point, i, a) => {
        // start control point
        const [cpsX, cpsY] = controlPointCalc(a[i - 1], a[i - 2], point);
        // end control point
        const [cpeX, cpeY] = controlPointCalc(point, a[i - 1], a[i + 1], true);

        return `C ${cpsX},${cpsY} ${cpeX},${cpeY} ${point[0]},${point[1]}`;
    };

    // Render the svg <path> element
    // I:  - points (array): points coordinates
    //     - command (function)
    //       I:  - point (array) [x,y]: current point coordinates
    //           - i (integer): index of 'point' in the array 'a'
    //           - a (array): complete array of points coordinates
    //       O:  - (string): a svg path command like 'L x,y' or C x2,y2 x1,y1 x,y'
    // O:  - (string): a Svg <path> element
    const svgPath = (pathPoints, command) => {
        const d = pathPoints.reduce(
            (acc, point, i, a) => (i === 0 ? `M ${point[0]},${point[1]}` : `${acc} ${command(point, i, a)}`),
            '',
        );
        return `<path class="js-about-path" d="${d}" fill="none" stroke="white" stroke-dasharray="10" stroke-width="3px" />`;
    };

    // Function composition

    // Position of a control point
    // I:  - current (array) [x, y]: coordinates
    //     - previous (array) [x, y]: coordinates
    //     - next (array) [x, y]: coordinates
    //     - reverse (boolean, optional): sets the direction
    // O:  - (array) [x,y]: coordinates
    const controlPointCalc = controlPoint(line, smoothing);

    // Bezier curve command
    // I:  - point (array) [x,y]: current point coordinates
    //     - i (integer): index of 'point' in the array 'a'
    //     - a (array): complete array of points coordinates
    // O:  - (string) 'C x2,y2 x1,y1 x,y': cubic bezier command
    const bezierCommandCalc = bezierCommand(controlPointCalc);

    // add path
    const svgPathParent = document.querySelector('.js-about-path');
    svgPathParent.innerHTML = svgPath(points, bezierCommandCalc);

    // add secondary mask path to handle dashes
    const svgPathMaskParent = document.querySelector('.js-about-path-mask');
    svgPathMaskParent.innerHTML = svgPath(points, bezierCommandCalc);

    animatePath();
};

const hero = () => (
    new Promise((resolve) => {
        const animationContainer = document.querySelector('.js-about-animation');
        const animation = lottie.loadAnimation({
            container: animationContainer,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            path: `${SWDLocalise.mediaUrl}/js/components/about/animation.json`,
        });

        animation.addEventListener('data_ready', () => {
            animationContainer.classList.remove('is-loading');
            resolve();
        });
    })
);

const onResizeHandler = debounce(() => makePath());

const init = () => {
    if (!pathContainer) return;

    hero()
        .then(preloadImages('.js-about-svg-container img'))
        .then(() => {
            makePath();
            scrollAnims();
        });

    window.addEventListener('resize', onResizeHandler);
};

export default init;
